import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { Alert, Button, ButtonGroup, Card, CardBody, Col, Row } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faPause, faPlus, faStore, faToolbox } from '@fortawesome/free-solid-svg-icons'
import Loader from '../../common/Loader'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage'
import { Toast } from '../../UI/toast/Toast'
import { deleteService, getMyProviderProfile, getMyServices, updateServiceStatus } from '../../../helpers/api/marketplace'
import ProviderStatusCard from './ProviderStatusCard'
import ProviderServiceCard from './services/ProviderServiceCard'

const FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Publicados' },
  { value: 'paused', label: 'Pausados' }
]

const StatTile = ({ icon, color, label, value }) => (
  <Card className="h-100">
    <CardBody className="d-flex align-items-center py-3">
      <div className={`d-flex align-items-center justify-content-center rounded-circle bg-soft-${color} text-${color} mr-3`} style={{ width: 40, height: 40 }}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <p className="fs--1 text-600 mb-0">{label}</p>
        <h4 className="mb-0">{value}</h4>
      </div>
    </CardBody>
  </Card>
)

const EmptyServices = ({ filtered }) => (
  <Card>
    <CardBody className="text-center py-5">
      <FontAwesomeIcon icon={faToolbox} size="3x" className="text-300 mb-3" />
      {filtered ? (
        <p className="text-600 mb-0">No tienes servicios en este estado.</p>
      ) : (
        <>
          <h5 className="mb-2">Aún no publicas servicios</h5>
          <p className="text-600 mb-4 mx-auto" style={{ maxWidth: 420 }}>
            Crea tu primera publicación con fotos de tus trabajos, el rango de precios y tu horario. Así los propietarios sabrán qué esperar antes de llamarte.
          </p>
          <Button tag={Link} to="/provider/services/create" color="primary">
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Publicar mi primer servicio
          </Button>
        </>
      )}
    </CardBody>
  </Card>
)

const ProviderDashboard = () => {
  const profileRequest = useSWRFetch('marketplace-provider-me', getMyProviderProfile)
  const servicesRequest = useSWRFetch('marketplace-provider-services', getMyServices)
  const [filter, setFilter] = useState('all')
  const [busyId, setBusyId] = useState(null)

  const profile = profileRequest.data?.data
  const services = useMemo(() => servicesRequest.data?.data || [], [servicesRequest.data])
  const visibleServices = filter === 'all' ? services : services.filter((service) => service.status === filter)
  const activeCount = services.filter((service) => service.status === 'active').length
  const marketplaceCount = services.filter((service) => service.is_visible).length

  const replaceService = (updated) =>
    servicesRequest.mutate(
      (current) => ({ ...current, data: current.data.map((service) => (service.id === updated.id ? updated : service)) }),
      false
    )

  const handleToggleStatus = async (service) => {
    const nextStatus = service.status === 'active' ? 'paused' : 'active'
    setBusyId(service.id)
    try {
      const { data } = await updateServiceStatus(service.id, nextStatus)
      replaceService(data)
      Toast.fire({
        type: 'success',
        title: nextStatus === 'paused' ? 'Servicio pausado: los propietarios ya no lo ven.' : 'Servicio publicado de nuevo.'
      })
    } catch (error) {
      handleErrorMessage(error)
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = (service) => {
    Swal.fire({
      title: '¿Eliminar este servicio?',
      text: `"${service.title}" dejará de aparecer en tu panel y en el Marketplace. Si solo quieres ocultarlo por un tiempo, usa Pausar.`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e63757',
      cancelButtonColor: '#bbb',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (!result.value) return
      setBusyId(service.id)
      try {
        await deleteService(service.id)
        servicesRequest.mutate(
          (current) => ({ ...current, data: current.data.filter((item) => item.id !== service.id) }),
          false
        )
        Toast.fire({ type: 'success', title: 'Servicio eliminado.' })
      } catch (error) {
        handleErrorMessage(error)
      } finally {
        setBusyId(null)
      }
    })
  }

  if (profileRequest.isLoading || servicesRequest.isLoading) {
    return (
      <Card>
        <Loader padding="py-5" />
      </Card>
    )
  }

  if (profileRequest.error || servicesRequest.error) {
    return (
      <Alert color="danger">
        No pudimos cargar tu panel de proveedor.
        <Button
          color="link"
          size="sm"
          className="ml-1 p-0 align-baseline"
          onClick={() => {
            profileRequest.mutate()
            servicesRequest.mutate()
          }}
        >
          Reintentar
        </Button>
      </Alert>
    )
  }

  return (
    <>
      {profile && <ProviderStatusCard profile={profile} />}

      <Row noGutters className="mb-3">
        <Col sm={4} className="pr-sm-2 mb-3 mb-sm-0">
          <StatTile icon={faStore} color="primary" label="Servicios publicados" value={activeCount} />
        </Col>
        <Col sm={4} className="px-sm-1 mb-3 mb-sm-0">
          <StatTile icon={faPause} color="secondary" label="Pausados" value={services.length - activeCount} />
        </Col>
        <Col sm={4} className="pl-sm-2">
          <StatTile icon={faEye} color="success" label="Visibles en el Marketplace" value={marketplaceCount} />
        </Col>
      </Row>

      <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 12 }}>
        <h4 className="mb-0 mr-auto">Mis servicios</h4>
        {services.length > 0 && (
          <ButtonGroup size="sm">
            {FILTERS.map((item) => (
              <Button
                key={item.value}
                color={filter === item.value ? 'primary' : 'falcon-default'}
                onClick={() => setFilter(item.value)}
              >
                {item.label}
              </Button>
            ))}
          </ButtonGroup>
        )}
        <Button tag={Link} to="/provider/services/create" color="primary" size="sm">
          <FontAwesomeIcon icon={faPlus} className="mr-1" />
          Publicar servicio
        </Button>
      </div>

      {visibleServices.length === 0 ? (
        <EmptyServices filtered={services.length > 0} />
      ) : (
        <Row>
          {visibleServices.map((service) => (
            <Col key={service.id} md={6} className="col-xxl-4 mb-3">
              <ProviderServiceCard
                service={service}
                busy={busyId === service.id}
                onToggleStatus={handleToggleStatus}
                onDelete={handleDelete}
              />
            </Col>
          ))}
        </Row>
      )}
    </>
  )
}

export default ProviderDashboard
